import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL search-param backed filter state. Reports pages put each filter on the
 * URL so deep links and the topbar "Back to Reports" return restore filters.
 *
 * Use the per-type getters below to decode a single param, and `setParam` /
 * `setParams` to write. Updates always use `replace: true` so filter changes
 * don't pile up in browser history.
 */
export function useUrlFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const getString = (key: string, defaultValue = ''): string =>
    searchParams.get(key) ?? defaultValue;

  const getInt = (key: string): number | undefined => {
    const raw = searchParams.get(key);
    if (raw == null || raw === '') return undefined;
    const n = parseInt(raw, 10);
    return isNaN(n) ? undefined : n;
  };

  const getBool = (key: string, defaultValue = false): boolean => {
    const raw = searchParams.get(key);
    if (raw == null) return defaultValue;
    return raw === '1' || raw === 'true';
  };

  // Comma-separated numeric IDs: "1,2,3" → [1,2,3]. Empty string → [].
  const getIntArray = (key: string): number[] => {
    const raw = searchParams.get(key);
    if (!raw) return [];
    return raw.split(',').map(s => parseInt(s, 10)).filter(n => !isNaN(n));
  };

  // Patch multiple params at once. Pass `null` or `''` to delete a key.
  // Value types: string | number | number[] | boolean | null | undefined.
  const setParams = useCallback((patch: Record<string, string | number | number[] | boolean | null | undefined>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(patch)) {
        if (value == null || value === '') {
          next.delete(key);
        } else if (Array.isArray(value)) {
          if (value.length === 0) next.delete(key);
          else next.set(key, value.join(','));
        } else if (typeof value === 'boolean') {
          if (value) next.set(key, '1');
          else next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return { searchParams, getString, getInt, getBool, getIntArray, setParams };
}
